import { z } from 'zod';
import { defineStateMachine } from '../common/state-machine.js';
/**
 * `CreditApplication` — `03-maquinas-de-estado.md` §7. El expediente tiene **fase** (dónde va el
 * proceso) y **estado** (situación de negocio). `REJECTED`, `WITHDRAWN` y `ABANDONED` son estados;
 * la fase se conserva para poder reabrir.
 */
export const APPLICATION_PHASES = [
    'DISCOVERY',
    'CONSENT',
    'IDENTITY',
    'BUSINESS_PROFILE',
    'EVIDENCE',
    'CASH_FLOW',
    'OFFER_SIMULATION',
    'SUBMITTED',
    'DECISION',
    'FORMALIZATION',
    'ACTIVE',
];
/** Fases previas a `SUBMITTED`: el agente (G) puede moverlas con `advance_phase`; admiten reingreso. */
export const PRE_SUBMISSION_PHASES = APPLICATION_PHASES.slice(0, 7);
const FORWARD = {
    DISCOVERY: ['CONSENT'],
    CONSENT: ['IDENTITY'],
    IDENTITY: ['BUSINESS_PROFILE'],
    BUSINESS_PROFILE: ['EVIDENCE'],
    EVIDENCE: ['CASH_FLOW'],
    CASH_FLOW: ['OFFER_SIMULATION'],
    OFFER_SIMULATION: ['SUBMITTED'],
    SUBMITTED: ['DECISION'],
    // DECISION → EVIDENCE: el analista pide más información.
    DECISION: ['FORMALIZATION', 'EVIDENCE'],
    FORMALIZATION: ['ACTIVE'],
    ACTIVE: [],
};
/** Regla de reingreso: cualquier fase previa a SUBMITTED puede volver a una anterior sin perder datos. */
function withReentry() {
    const table = {};
    APPLICATION_PHASES.forEach((phase, index) => {
        const back = PRE_SUBMISSION_PHASES.includes(phase) ? APPLICATION_PHASES.slice(0, index) : [];
        table[phase] = [...new Set([...FORWARD[phase], ...back])];
    });
    return table;
}
export const ApplicationPhaseMachine = defineStateMachine({
    name: 'ApplicationPhase',
    states: APPLICATION_PHASES,
    initial: ['DISCOVERY'],
    terminal: ['ACTIVE'],
    invalidTransitionCode: 'PZ-APP-007',
    transitions: withReentry(),
});
export const ApplicationPhaseSchema = ApplicationPhaseMachine.schema.meta({
    id: 'ApplicationPhase',
});
/** Quién puede mover cada transición de fase (G = agente, nunca en DECISION). */
export const PhaseActorSchema = z
    .enum(['AGENT', 'CUSTOMER', 'ANALYST', 'SYSTEM'])
    .meta({ id: 'PhaseActor' });
/** El agente solo mueve fases hasta `OFFER_SIMULATION` (validando criterios de salida en código). */
export const AGENT_MAX_PHASE = 'OFFER_SIMULATION';
export const ApplicationStatusMachine = defineStateMachine({
    name: 'ApplicationStatus',
    states: [
        'IN_PROGRESS',
        'SUBMITTED',
        'APPROVED',
        'REJECTED',
        'COUNTER_OFFERED',
        'WITHDRAWN',
        'ABANDONED',
        'ACTIVE',
    ],
    initial: ['IN_PROGRESS'],
    terminal: ['REJECTED', 'WITHDRAWN', 'ACTIVE'],
    invalidTransitionCode: 'PZ-APP-007',
    transitions: {
        // REJECTED desde IN_PROGRESS: no elegible por reglas duras (DISCOVERY) o listas (IDENTITY).
        IN_PROGRESS: ['SUBMITTED', 'REJECTED', 'WITHDRAWN', 'ABANDONED'],
        // SUBMITTED → IN_PROGRESS: el analista pide más información (fase vuelve a EVIDENCE).
        SUBMITTED: ['APPROVED', 'REJECTED', 'COUNTER_OFFERED', 'IN_PROGRESS', 'WITHDRAWN'],
        COUNTER_OFFERED: ['APPROVED', 'WITHDRAWN', 'REJECTED'],
        APPROVED: ['ACTIVE', 'WITHDRAWN'],
        // Reapertura dentro de 90 días restaurando el último memory_checkpoint.
        ABANDONED: ['IN_PROGRESS'],
        REJECTED: [],
        WITHDRAWN: [],
        ACTIVE: [],
    },
});
export const ApplicationStatusSchema = ApplicationStatusMachine.schema.meta({
    id: 'ApplicationStatus',
});
export const APPLICATION_TIMINGS = {
    abandonAfterDays: 30,
    reminderDays: [2, 7, 20],
    reopenWithinDays: 90,
    decisionSlaBusinessDays: 1,
};
/** `EvidenceDocument` — §8. */
export const EvidenceDocumentMachine = defineStateMachine({
    name: 'EvidenceDocument',
    states: ['UPLOADED', 'ANALYZING', 'ACCEPTED', 'NEEDS_REPLACEMENT', 'REJECTED', 'SUPERSEDED'],
    initial: ['UPLOADED'],
    terminal: ['SUPERSEDED'],
    transitions: {
        UPLOADED: ['ANALYZING'],
        // ANALYZING → UPLOADED: error técnico, reintento (máx. 3).
        ANALYZING: ['ACCEPTED', 'NEEDS_REPLACEMENT', 'REJECTED', 'UPLOADED'],
        NEEDS_REPLACEMENT: ['SUPERSEDED'],
        // Anulaciones del analista (A).
        REJECTED: ['ACCEPTED'],
        ACCEPTED: ['REJECTED'],
        SUPERSEDED: [],
    },
});
export const EvidenceDocumentStatusSchema = EvidenceDocumentMachine.schema.meta({
    id: 'EvidenceDocumentStatus',
});
export const DOCUMENT_ANALYSIS = {
    timeoutSeconds: 120,
    /** Lo que `review_document` espera una revisión en curso antes de responder "sigue en revisión". */
    inlineWaitSeconds: 75,
    maxTechnicalRetries: 3,
    maxSizeBytes: 15 * 1024 * 1024,
};
/** Hojas de cálculo (registro de ingresos y gastos): se leen como texto. `.xls` antiguo no. */
export const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const CSV_MIME_TYPE = 'text/csv';
export const SPREADSHEET_MIME_TYPES = [XLSX_MIME_TYPE, CSV_MIME_TYPE];
export const ALLOWED_DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
    XLSX_MIME_TYPE,
    CSV_MIME_TYPE,
];
/** Extensión con la que se guarda cada formato permitido. */
export const DOCUMENT_EXTENSION = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/heic': 'heic',
    'image/heif': 'heif',
    [XLSX_MIME_TYPE]: 'xlsx',
    [CSV_MIME_TYPE]: 'csv',
};
/**
 * Tipo del archivo según su extensión cuando el navegador no lo da bien: Excel en Windows reporta
 * un `.csv` como `application/vnd.ms-excel`, y algunos teléfonos mandan HEIC o XLSX sin tipo.
 */
export function normalizeDocumentMime(fileName, mimeType) {
    const ext = /\.([a-z0-9]+)$/i.exec(fileName)?.[1]?.toLowerCase();
    if (ext === 'xlsx')
        return XLSX_MIME_TYPE;
    if (ext === 'csv')
        return CSV_MIME_TYPE;
    const generic = mimeType === '' || mimeType === 'application/octet-stream';
    if (generic && ext === 'pdf')
        return 'application/pdf';
    if (generic && (ext === 'heic' || ext === 'heif'))
        return `image/${ext}`;
    return mimeType;
}
export const DocumentVerdictSchema = z
    .enum(['ACCEPTED', 'NEEDS_REPLACEMENT', 'REJECTED'])
    .meta({ id: 'DocumentVerdict' });
export const OfferStatusSchema = z
    .enum(['PRESENTED', 'ACCEPTED', 'DECLINED', 'EXPIRED'])
    .meta({ id: 'OfferStatus' });
export const FormalizationStatusSchema = z
    .enum(['PENDING_SIGNATURE', 'SIGNED', 'LINE_OPENED', 'LINKED', 'COMPLETED', 'FAILED'])
    .meta({ id: 'FormalizationStatus' });
export const RecommendationSchema = z
    .enum(['APPROVE', 'REJECT', 'REVIEW', 'COUNTER_OFFER'])
    .meta({ id: 'Recommendation' });
export const CreditDecisionOutcomeSchema = z
    .enum(['APPROVED', 'REJECTED', 'COUNTER_OFFER'])
    .meta({ id: 'CreditDecisionOutcome' });
/** Quién decide: motor por política (S) o analista (A). Nunca el agente. */
export const DecisionMakerSchema = z.enum(['POLICY_ENGINE', 'ANALYST']);
//# sourceMappingURL=states.js.map